import type {
  ProjectDepartment,
  ProjectPhase,
  ProjectTask,
  TaskGroup,
  TaskStatus,
  TimberProject,
} from '../types';
import { isSimulatingUnderContract } from './simulateUnderContract';
import {
  resolveGroupTimelineSection,
  type GroupTimelineProjectLabel,
  formatGroupTimelineSectionLabel,
} from './groupTimeline';
import {
  UNASSIGNED_SUPPLIER_KEY,
  UNASSIGNED_SUPPLIER_LABEL,
  type GroupTimelineSupplierResolution,
} from './groupTimelineSupplier';
import {
  isGroupTimelineConflictFamily,
  type GroupTimelineConflictIssues,
} from './groupTimelineConflicts';

export type GroupTimelineSimulatedFilter = 'all' | 'simulated' | 'real';

export type GroupTimelineDetailFilter = {
  projectIds: string[];
  sectionIds: string[];
  phases: ProjectPhase[];
  statuses: TaskStatus[];
  departments: ProjectDepartment[];
  supplierOrgIds: string[];
  simulated: GroupTimelineSimulatedFilter;
  /** Scoped parent task ids (subtasks stay unless a supplier filter is also on). */
  taskIds: string[];
};

export const EMPTY_GROUP_TIMELINE_FILTER: GroupTimelineDetailFilter = {
  projectIds: [],
  sectionIds: [],
  phases: [],
  statuses: [],
  departments: [],
  supplierOrgIds: [],
  simulated: 'all',
  taskIds: [],
};

export const GROUP_TIMELINE_FILTER_STORAGE_KEY = 'pm-hub-group-timeline-filter';

const PHASES: ProjectPhase[] = ['estimating', 'preconstruction', 'contracted'];
const STATUSES: TaskStatus[] = ['pending', 'in-progress', 'review', 'completed', 'blocked', 'on-hold'];
const DEPARTMENTS: ProjectDepartment[] = [
  'preconstruction',
  'design',
  'procurement',
  'production',
  'installation',
];

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    if (typeof item !== 'string') continue;
    const id = item.trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

export function parseGroupTimelineDetailFilter(raw: unknown): GroupTimelineDetailFilter {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_GROUP_TIMELINE_FILTER };
  const data = raw as Record<string, unknown>;
  const simulated = data.simulated === 'simulated' || data.simulated === 'real'
    ? data.simulated
    : 'all';
  return {
    projectIds: asStringArray(data.projectIds),
    sectionIds: asStringArray(data.sectionIds),
    phases: asStringArray(data.phases).filter((p): p is ProjectPhase =>
      (PHASES as string[]).includes(p),
    ),
    statuses: asStringArray(data.statuses).filter((s): s is TaskStatus =>
      (STATUSES as string[]).includes(s),
    ),
    departments: asStringArray(data.departments).filter((d): d is ProjectDepartment =>
      (DEPARTMENTS as string[]).includes(d),
    ),
    supplierOrgIds: asStringArray(data.supplierOrgIds),
    simulated,
    taskIds: asStringArray(data.taskIds),
  };
}

export function readStoredGroupTimelineFilter(): GroupTimelineDetailFilter {
  try {
    const raw = sessionStorage.getItem(GROUP_TIMELINE_FILTER_STORAGE_KEY);
    if (!raw) return { ...EMPTY_GROUP_TIMELINE_FILTER };
    return parseGroupTimelineDetailFilter(JSON.parse(raw));
  } catch {
    return { ...EMPTY_GROUP_TIMELINE_FILTER };
  }
}

export function storeGroupTimelineFilter(filter: GroupTimelineDetailFilter): void {
  try {
    sessionStorage.setItem(GROUP_TIMELINE_FILTER_STORAGE_KEY, JSON.stringify(filter));
  } catch {
    /* ignore */
  }
}

export function countActiveGroupTimelineFilters(filter: GroupTimelineDetailFilter): number {
  let n = 0;
  if (filter.projectIds.length) n += 1;
  if (filter.sectionIds.length) n += 1;
  if (filter.phases.length) n += 1;
  if (filter.statuses.length) n += 1;
  if (filter.departments.length) n += 1;
  if (filter.supplierOrgIds.length) n += 1;
  if (filter.simulated !== 'all') n += 1;
  if (filter.taskIds.length) n += 1;
  return n;
}

/** Projects, family sections, and scoped parents from a badge’s issue list. */
export function detailFilterFromConflictIssues(
  issues: GroupTimelineConflictIssues,
): GroupTimelineDetailFilter {
  const projectIds = new Set<string>();
  const sectionIds = new Set<string>();
  const taskIds = new Set<string>();
  for (const miss of issues.missing) {
    if (miss.projectId) projectIds.add(miss.projectId);
    if (miss.taskId) taskIds.add(miss.taskId);
    sectionIds.add(miss.family);
  }
  for (const pair of issues.pairs) {
    if (pair.a.projectId) projectIds.add(pair.a.projectId);
    if (pair.b.projectId) projectIds.add(pair.b.projectId);
    if (pair.a.taskId) taskIds.add(pair.a.taskId);
    if (pair.b.taskId) taskIds.add(pair.b.taskId);
    sectionIds.add(pair.family);
  }
  return {
    ...EMPTY_GROUP_TIMELINE_FILTER,
    projectIds: Array.from(projectIds).sort(),
    sectionIds: Array.from(sectionIds).filter(isGroupTimelineConflictFamily).sort(),
    taskIds: Array.from(taskIds).sort(),
  };
}

export function toggleFilterValue<T extends string>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

function supplierKeysForTask(
  task: ProjectTask,
  byTaskId?: Map<string, GroupTimelineSupplierResolution>,
): string[] {
  if (!task.id || !byTaskId) return [UNASSIGNED_SUPPLIER_KEY];
  const res = byTaskId.get(task.id);
  if (!res?.orgIds.length) return [UNASSIGNED_SUPPLIER_KEY];
  return res.orgIds.filter(Boolean);
}

function isUnassignedSupplierKeys(keys: string[]): boolean {
  return keys.length === 0 || keys.every((key) => key === UNASSIGNED_SUPPLIER_KEY);
}

function supplierKeysForFilter(
  task: ProjectTask,
  byTaskId?: Map<string, GroupTimelineSupplierResolution>,
  inheritedKeys?: string[],
): string[] {
  const own = supplierKeysForTask(task, byTaskId);
  if (!isUnassignedSupplierKeys(own) || !inheritedKeys?.length || isUnassignedSupplierKeys(inheritedKeys)) {
    return own;
  }
  return inheritedKeys;
}

export function applyGroupTimelineDetailFilter(params: {
  filter: GroupTimelineDetailFilter;
  projects: TimberProject[];
  groups: TaskGroup[];
  tasks: ProjectTask[];
  supplierByTaskId?: Map<string, GroupTimelineSupplierResolution>;
  simulatedProjectIds?: readonly string[];
}): { projects: TimberProject[]; groups: TaskGroup[]; tasks: ProjectTask[] } {
  const { filter, supplierByTaskId, simulatedProjectIds } = params;
  let projects = params.projects.filter((p) => p.id && p.status !== 'completed');

  if (filter.projectIds.length) {
    const allow = new Set(filter.projectIds);
    projects = projects.filter((p) => p.id && allow.has(p.id));
  }
  if (filter.phases.length) {
    const allow = new Set(filter.phases);
    projects = projects.filter((p) => {
      const phase = p.phase === undefined ? 'contracted' : p.phase;
      return allow.has(phase);
    });
  }
  if (filter.simulated !== 'all') {
    projects = projects.filter((p) => {
      const sim = isSimulatingUnderContract(p, simulatedProjectIds);
      return filter.simulated === 'simulated' ? sim : !sim;
    });
  }

  const projectIds = new Set(projects.map((p) => p.id!).filter(Boolean));
  let groups = params.groups.filter((g) => g.id && projectIds.has(g.projectId));
  if (filter.sectionIds.length) {
    const allow = new Set(filter.sectionIds);
    groups = groups.filter((g) => allow.has(resolveGroupTimelineSection(g.name).id));
  }
  const groupIds = new Set(groups.map((g) => g.id!).filter(Boolean));

  const childrenByParent = new Map<string, ProjectTask[]>();
  for (const t of params.tasks) {
    if (!t.parentTaskId) continue;
    const list = childrenByParent.get(t.parentTaskId) ?? [];
    list.push(t);
    childrenByParent.set(t.parentTaskId, list);
  }

  const taskPasses = (t: ProjectTask, inheritedSupplierKeys?: string[]): boolean => {
    if (!projectIds.has(t.projectId)) return false;
    if (t.groupId && !groupIds.has(t.groupId) && !t.parentTaskId) return false;
    if (filter.statuses.length && !filter.statuses.includes(t.status)) return false;
    if (filter.departments.length && !filter.departments.includes(t.department)) return false;
    if (filter.supplierOrgIds.length) {
      const keys = supplierKeysForFilter(t, supplierByTaskId, inheritedSupplierKeys);
      if (!keys.some((key) => filter.supplierOrgIds.includes(key))) return false;
    }
    return true;
  };

  const keepIds = new Set<string>();
  const allowTaskIds = filter.taskIds.length ? new Set(filter.taskIds) : null;
  const supplierFilterOn = filter.supplierOrgIds.length > 0;
  for (const t of params.tasks) {
    if (t.parentTaskId) continue;
    if (allowTaskIds && (!t.id || !allowTaskIds.has(t.id))) continue;
    const kids = t.id ? (childrenByParent.get(t.id) ?? []) : [];
    const parentSupplierKeys = supplierKeysForTask(t, supplierByTaskId);
    const matchingKids = kids.filter((child) => taskPasses(child, parentSupplierKeys));
    if (!taskPasses(t) && matchingKids.length === 0) continue;
    if (t.id) keepIds.add(t.id);
    const keepKids = supplierFilterOn ? matchingKids : kids;
    for (const child of keepKids) {
      if (child.id) keepIds.add(child.id);
    }
  }

  const tasks = params.tasks.filter((t) => t.id && keepIds.has(t.id));
  const usedGroupIds = new Set(
    tasks.filter((t) => !t.parentTaskId && t.groupId).map((t) => t.groupId!),
  );
  groups = groups.filter((g) => g.id && usedGroupIds.has(g.id));
  const usedProjectIds = new Set(tasks.map((t) => t.projectId));
  projects = projects.filter((p) => p.id && usedProjectIds.has(p.id));

  return { projects, groups, tasks };
}

export type GroupTimelineFilterProjectOption = { id: string; label: string };
export type GroupTimelineFilterSectionOption = { id: string; name: string };
export type GroupTimelineFilterSupplierOption = { id: string; name: string };

export function buildGroupTimelineFilterOptions(params: {
  projects: TimberProject[];
  groups: TaskGroup[];
  tasks: ProjectTask[];
  projectLabels?: Map<string, GroupTimelineProjectLabel>;
  supplierByTaskId?: Map<string, GroupTimelineSupplierResolution>;
  supplierNameByOrgId?: Map<string, string>;
}): {
  projects: GroupTimelineFilterProjectOption[];
  sections: GroupTimelineFilterSectionOption[];
  suppliers: GroupTimelineFilterSupplierOption[];
} {
  const active = params.projects.filter((p) => p.id && p.status !== 'completed');
  const projectIds = new Set(active.map((p) => p.id!));

  const projects = active
    .map((p) => {
      const label = p.id ? params.projectLabels?.get(p.id) : undefined;
      return {
        id: p.id!,
        label: formatGroupTimelineSectionLabel({
          projectNumber: label?.projectNumber,
          descriptor: label?.descriptor,
          projectName: p.name,
        }),
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  const sectionMap = new Map<string, GroupTimelineFilterSectionOption>();
  for (const g of params.groups) {
    if (!g.id || !projectIds.has(g.projectId)) continue;
    const section = resolveGroupTimelineSection(g.name);
    if (!sectionMap.has(section.id)) {
      sectionMap.set(section.id, { id: section.id, name: section.name });
    }
  }
  const sections = Array.from(sectionMap.values()).sort((a, b) => a.name.localeCompare(b.name));

  const supplierIds = new Set<string>();
  for (const t of params.tasks) {
    if (!projectIds.has(t.projectId)) continue;
    for (const key of supplierKeysForTask(t, params.supplierByTaskId)) {
      supplierIds.add(key);
    }
  }
  const suppliers = Array.from(supplierIds)
    .map((id) => ({
      id,
      name: id === UNASSIGNED_SUPPLIER_KEY
        ? UNASSIGNED_SUPPLIER_LABEL
        : (params.supplierNameByOrgId?.get(id) || id),
    }))
    .sort((a, b) => {
      if (a.id === UNASSIGNED_SUPPLIER_KEY) return 1;
      if (b.id === UNASSIGNED_SUPPLIER_KEY) return -1;
      return a.name.localeCompare(b.name);
    });

  return { projects, sections, suppliers };
}
